import { BACKSPACE } from '@angular/cdk/keycodes';
import { Directive, DoCheck, ElementRef, Inject, InjectFlags, InjectionToken, Injector, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  NgControl,
  NgForm,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { CanUpdateErrorStateCtor, ErrorStateMatcher, mixinErrorState } from '@ngx-simple/core/common-behaviors';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { SafeAny } from '@ngx-simple/core/types';
import { DateRange, DateSelectionModelChange, SimDateSelectionModel } from './date-selection-model';
import { SimDatepickerInputBase } from './datepicker-input-base';

/** 可用于过滤日历中日期的功能 */
export type DateFilterFn<D> = (date: D | null) => boolean;

/** 用于包裹`simStartDate`和`simEndDate`的父组件。 */
export interface DateRangeInputParent<D> {
  id: string;
  format: string;
  picker: string;
  min: D | null;
  max: D | null;
  focusedInput: DateRangeInputPartBase<D>;
  dateFilter: DateFilterFn<D>;
  _startInput: DateRangeInputPartBase<D>;
  _endInput: DateRangeInputPartBase<D>;
  _groupDisabled: boolean;
  _handleChildValueChange: () => void;
  _openDatePicker: () => void;
}

/** 用于为`simStartDate`和`simEndDate`提供sim-date-range-input组件，没有循环依赖性。 */
export const SIM_DATE_RANGE_INPUT_PARENT = new InjectionToken<DateRangeInputParent<unknown>>('SIM_DATE_RANGE_INPUT_PARENT');

/** 可以在sim-date-range-input内部投影的各个`simStartDate`和`simEndDate`的基类 */
@Directive({})
abstract class DateRangeInputPartBase<D> extends SimDatepickerInputBase<DateRange<D>> implements OnInit, DoCheck {
  ngControl: NgControl;

  protected abstract _validator: ValidatorFn | null;

  format = this._rangeInput.format;
  picker = this._rangeInput.picker;

  constructor(
    @Inject(SIM_DATE_RANGE_INPUT_PARENT) public _rangeInput: DateRangeInputParent<D>,
    elementRef: ElementRef<HTMLInputElement>,
    private _injector: Injector,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() dateAdapter: SimDateAdapter<D>,
    @Optional() public parentForm: NgForm,
    @Optional() public parentFormGroup: FormGroupDirective
  ) {
    super(elementRef, dateAdapter);
  }

  abstract updateErrorState(): void;

  ngOnInit(): void {
    const ngControl = this._injector.get(NgControl as SafeAny, null, InjectFlags.Self);

    if (ngControl) {
      this.ngControl = ngControl;
    }
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  /** 获取输入是否为空 */
  isEmpty(): boolean {
    return this._elementRef.nativeElement.value.length === 0;
  }

  /** 聚焦输入框 */
  focus(): void {
    this._elementRef.nativeElement.focus();
  }

  /** 处理输入上的focus事件 */
  _onFocus() {
    this._rangeInput.focusedInput = this;
  }

  _registerModel(model: SimDateSelectionModel<DateRange<D>, D>) {
    Promise.resolve().then(() => super._registerModel(model));
  }

  _removeValue() {
    this._elementRef.nativeElement.value = '';
  }

  /** 从date-range-input获取最小日期 */
  _getMinDate() {
    return this._rangeInput.min;
  }

  /** 从date-range-input获取最大日期 */
  _getMaxDate() {
    return this._rangeInput.max;
  }

  /** 从date-range-input获取过滤方法 */
  _getDateFilter() {
    return this._rangeInput.dateFilter;
  }
  protected abstract _assignValueToModel(value: D | null): void;
  protected abstract _getValueFromModel(modelValue: DateRange<D>): D | null;

  protected _openPopup() {
    this._rangeInput._openDatePicker();
  }

  protected _outsideValueChanged = () => {
    this._validatorOnChange();
  };
}

const _DateRangeInputBase: CanUpdateErrorStateCtor & typeof DateRangeInputPartBase = mixinErrorState(DateRangeInputPartBase as SafeAny);

@Directive({
  selector: 'input[simStartDate]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: SimStartDateDirective, multi: true },
    { provide: NG_VALIDATORS, useExisting: SimStartDateDirective, multi: true }
  ],
  host: {
    class: 'sim-start-date sim-date-range-input-inner',
    '[disabled]': 'disabled',
    '(input)': '_onInput($event.target.value)',
    '(change)': '_onChange()',
    '(keydown)': '_onKeydown($event)',
    '[attr.min]': '_getMinDate() ? _dateAdapter.toIso8601(_getMinDate()) : null',
    '[attr.max]': '_getMaxDate() ? _dateAdapter.toIso8601(_getMaxDate()) : null',
    '(blur)': '_onBlur()',
    '(focus)': '_onFocus()',
    type: 'text'
  }
})
export class SimStartDateDirective<D> extends _DateRangeInputBase<D> {
  protected _validator: ValidatorFn | null;
  constructor(
    @Inject(SIM_DATE_RANGE_INPUT_PARENT) _rangeInput: DateRangeInputParent<D>,
    elementRef: ElementRef,
    injector: Injector,
    defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() dateAdapter: SimDateAdapter<D>,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective
  ) {
    super(_rangeInput, elementRef, injector, defaultErrorStateMatcher, dateAdapter, parentForm, parentFormGroup);
    this._validator = Validators.compose([...super._getValidators(), this._startValidator]);
  }

  protected _getValueFromModel(modelValue: DateRange<D>) {
    return modelValue.start;
  }

  protected _assignValueToModel(value: D | null) {
    if (this._model) {
      const range = new DateRange(value, this._model.selection.end);
      this._model.updateSelection(range, this);
    }
  }

  protected _formatValue(value: D | null) {
    super._formatValue(value);

    this._rangeInput._handleChildValueChange();
  }

  protected _canEmitChangeEvent = (event: DateSelectionModelChange<DateRange<D>>): boolean => {
    return event.source !== this._rangeInput._endInput;
  };

  private _startValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const start = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(control.value));
    const end = this._model ? this._model.selection.end : null;
    return !start || !end || this._dateAdapter.compareDate(start, end) <= 0 ? null : { simStartDateInvalid: { end: end, actual: start } };
  };
}

@Directive({
  selector: 'input[simEndDate]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: SimEndDateDirective, multi: true },
    { provide: NG_VALIDATORS, useExisting: SimEndDateDirective, multi: true }
  ],
  host: {
    class: 'sim-end-date sim-date-range-input-inner',
    '[disabled]': 'disabled',
    '(input)': '_onInput($event.target.value)',
    '(change)': '_onChange()',
    '(keydown)': '_onKeydown($event)',
    '[attr.min]': '_getMinDate() ? _dateAdapter.toIso8601(_getMinDate()) : null',
    '[attr.max]': '_getMaxDate() ? _dateAdapter.toIso8601(_getMaxDate()) : null',
    '(blur)': '_onBlur()',
    '(focus)': '_onFocus()',
    type: 'text'
  }
})
export class SimEndDateDirective<D> extends _DateRangeInputBase<D> {
  protected _validator: ValidatorFn | null;

  constructor(
    @Inject(SIM_DATE_RANGE_INPUT_PARENT) _rangeInput: DateRangeInputParent<D>,
    elementRef: ElementRef,
    injector: Injector,
    defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() dateAdapter: SimDateAdapter<D>,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective
  ) {
    super(_rangeInput, elementRef, injector, defaultErrorStateMatcher, dateAdapter, parentForm, parentFormGroup);
    this._validator = Validators.compose([...super._getValidators(), this._endValidator]);
  }

  protected _getValueFromModel(modelValue: DateRange<D>) {
    return modelValue.end;
  }

  protected _assignValueToModel(value: D | null) {
    if (this._model) {
      const range = new DateRange(this._model.selection.start, value);
      this._model.updateSelection(range, this);
    }
  }

  protected _canEmitChangeEvent = (event: DateSelectionModelChange<DateRange<D>>): boolean => {
    return event.source !== this._rangeInput._startInput;
  };

  _onKeydown(event: KeyboardEvent) {
    // 如果用户正在对空的结束输入按退格键，则将焦点移回开始位置。
    // tslint:disable-next-line: deprecation
    if (event.keyCode === BACKSPACE && !this._elementRef.nativeElement.value) {
      this._rangeInput._startInput.focus();
    }

    super._onKeydown(event);
  }

  private _endValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const end = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(control.value));
    const start = this._model ? this._model.selection.start : null;
    return !end || !start || this._dateAdapter.compareDate(end, start) >= 0 ? null : { simEndDateInvalid: { start: start, actual: end } };
  };
}
